import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useMarketContext } from '../context/MarketDataContext';
import { getValidToken } from '../utils/auth';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const API_URL = process.env.REACT_APP_API_URL || '';

// GICS sector colors
const SECTOR_COLORS = {
  'Information Technology': '#8b5cf6',
  'Health Care': '#06b6d4',
  'Financials': '#f59e0b',
  'Consumer Discretionary': '#ec4899',
  'Communication Services': '#3b82f6',
  'Industrials': '#6b7280',
  'Consumer Staples': '#10b981',
  'Energy': '#ef4444',
  'Utilities': '#a78bfa',
  'Real Estate': '#f97316',
  'Materials': '#14b8a6',
};

function getScoreColor(score) {
  if (score > 30) return '#22c55e';
  if (score > 10) return '#86efac';
  if (score > -10) return '#9ca3af';
  if (score > -30) return '#fca5a5';
  return '#ef4444';
}

function getScoreLabel(score) {
  if (score > 30) return 'Strong Bullish';
  if (score > 10) return 'Bullish';
  if (score > -10) return 'Neutral';
  if (score > -30) return 'Bearish';
  return 'Strong Bearish';
}

export default function SP500Momentum() {
  const { tickers } = useMarketContext();
  const [components, setComponents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [scoreHistory, setScoreHistory] = useState([]);
  const prevScoreRef = useRef(null);

  // Load SP500 components once
  useEffect(() => {
    async function load() {
      try {
        const token = await getValidToken();
        const res = await fetch(`${API_URL}/api/sp500/components`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error('Failed to load S&P 500 components');
        const data = await res.json();
        setComponents(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  // Compute weighted direction score
  const { score, matchedCount, contributors, sectorBreakdown } = useMemo(() => {
    if (components.length === 0) return { score: 0, matchedCount: 0, contributors: [], sectorBreakdown: [] };

    let rawScore = 0;
    let totalWeight = 0;
    const contribs = [];
    const sectorMap = {};

    for (const comp of components) {
      const t = tickers[comp.symbol];
      if (!t || t.changePercent === undefined) continue;

      const contribution = t.changePercent * comp.weight * 100; // weight is decimal
      rawScore += contribution;
      totalWeight += comp.weight;

      contribs.push({
        symbol: comp.symbol,
        company: comp.company_name,
        sector: comp.sector,
        weight: comp.weight,
        changePercent: t.changePercent,
        contribution,
        price: t.price,
      });

      if (!sectorMap[comp.sector]) {
        sectorMap[comp.sector] = { sector: comp.sector, contribution: 0, count: 0, matched: 0 };
      }
      sectorMap[comp.sector].contribution += contribution;
      sectorMap[comp.sector].matched++;
    }

    // Count total per sector
    for (const comp of components) {
      if (!sectorMap[comp.sector]) {
        sectorMap[comp.sector] = { sector: comp.sector, contribution: 0, count: 0, matched: 0 };
      }
      sectorMap[comp.sector].count++;
    }

    // Normalize score to -100 to +100 range
    // Max possible raw score would be if all stocks moved 100% * their weight
    // In practice, clamp to a reasonable range. Use totalWeight as normalizer.
    const normalizedScore = totalWeight > 0
      ? Math.max(-100, Math.min(100, rawScore / totalWeight))
      : 0;

    // Sort contributors by absolute contribution
    contribs.sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

    // Sort sectors by absolute contribution
    const sectors = Object.values(sectorMap)
      .sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

    return {
      score: normalizedScore,
      matchedCount: contribs.length,
      contributors: contribs.slice(0, 20),
      sectorBreakdown: sectors,
    };
  }, [components, tickers]);

  // Record score history every 30 seconds
  const recordScore = useCallback(() => {
    if (matchedCount === 0) return;
    setScoreHistory(prev => {
      const now = new Date();
      const point = {
        time: now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        score: Math.round(score * 100) / 100,
        ts: now.getTime(),
      };
      // Keep only last 1 hour (120 points at 30s intervals)
      const oneHourAgo = now.getTime() - 3600000;
      const filtered = [...prev.filter(p => p.ts > oneHourAgo), point];
      return filtered;
    });
  }, [score, matchedCount]);

  useEffect(() => {
    // Record immediately when score changes meaningfully
    if (matchedCount > 0 && prevScoreRef.current !== score) {
      prevScoreRef.current = score;
      recordScore();
    }
    const interval = setInterval(recordScore, 30000);
    return () => clearInterval(interval);
  }, [recordScore, score, matchedCount]);

  const scoreColor = getScoreColor(score);
  const scoreLabel = getScoreLabel(score);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">S&P 500 Momentum</h1>
        <div className="text-gray-500">Loading components...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">S&P 500 Momentum</h1>
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">S&P 500 Momentum</h1>
          <p className="text-sm text-gray-500 mt-1">
            {matchedCount} / {components.length} components with live data
          </p>
        </div>
      </div>

      {/* Score Card + Mini Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-5">
        {/* Main Score */}
        <div className="bg-gray-900 rounded-2xl p-6 text-center flex flex-col items-center justify-center">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Weighted Direction Score</p>
          <div className="relative mb-3">
            <div className="text-6xl font-bold tabular-nums" style={{ color: scoreColor }}>
              {matchedCount > 0 ? (score >= 0 ? '+' : '') + score.toFixed(1) : '—'}
            </div>
          </div>
          <div
            className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-semibold"
            style={{ backgroundColor: scoreColor + '20', color: scoreColor }}
          >
            <span className="inline-block w-2 h-2 rounded-full" style={{ backgroundColor: scoreColor }} />
            {matchedCount > 0 ? scoreLabel : 'Waiting for data'}
          </div>
          {/* Scale bar */}
          <div className="w-full mt-5">
            <div className="relative h-2 rounded-full bg-gray-800 overflow-hidden">
              <div
                className="absolute top-0 h-full rounded-full transition-all duration-500"
                style={{
                  backgroundColor: scoreColor,
                  left: `${Math.min(50, 50 + (score / 2))}%`,
                  width: `${Math.abs(score / 2)}%`,
                }}
              />
              {/* Center marker */}
              <div className="absolute top-0 left-1/2 w-0.5 h-full bg-gray-600" />
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-gray-500">
              <span>-100</span>
              <span>0</span>
              <span>+100</span>
            </div>
          </div>
        </div>

        {/* Mini Chart */}
        <div className="lg:col-span-2 bg-gray-900 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3">Score — Last 1 Hour</p>
          {scoreHistory.length < 2 ? (
            <div className="h-40 flex items-center justify-center text-gray-500 text-sm">
              Collecting data points...
            </div>
          ) : (
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={scoreHistory}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="time" tick={{ fontSize: 10, fill: '#9ca3af' }} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 10, fill: '#9ca3af' }} domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#9ca3af' }}
                    formatter={(v) => [v.toFixed(2), 'Score']}
                  />
                  <defs>
                    <linearGradient id="scoreGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={scoreColor} stopOpacity={0.3} />
                      <stop offset="100%" stopColor={scoreColor} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="score"
                    stroke={scoreColor}
                    fill="url(#scoreGrad)"
                    strokeWidth={2}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Sector Breakdown + Top Contributors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Sector Breakdown */}
        <div className="bg-gray-900 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">Sector Breakdown</p>
          {matchedCount === 0 ? (
            <div className="text-gray-500 text-sm text-center py-8">Waiting for market data...</div>
          ) : (
            <div className="space-y-2.5">
              {sectorBreakdown.map((s) => {
                const color = SECTOR_COLORS[s.sector] || '#6b7280';
                const isPositive = s.contribution >= 0;
                // Normalize bar width relative to max contribution
                const maxContrib = Math.max(...sectorBreakdown.map(x => Math.abs(x.contribution)), 0.01);
                const barWidth = Math.abs(s.contribution) / maxContrib * 100;
                return (
                  <div key={s.sector} className="group">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: color }} />
                        <span className="text-sm text-gray-300">{s.sector}</span>
                        <span className="text-[10px] text-gray-500">{s.matched}/{s.count}</span>
                      </div>
                      <span className={`text-sm font-semibold tabular-nums ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                        {isPositive ? '+' : ''}{s.contribution.toFixed(2)}
                      </span>
                    </div>
                    <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${barWidth}%`,
                          backgroundColor: isPositive ? '#22c55e' : '#ef4444',
                          opacity: 0.7,
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Top 20 Contributors */}
        <div className="bg-gray-900 rounded-2xl p-5">
          <p className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-4">Top 20 Contributors</p>
          {matchedCount === 0 ? (
            <div className="text-gray-500 text-sm text-center py-8">Waiting for market data...</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider">
                    <th className="pb-2">Symbol</th>
                    <th className="pb-2 text-right">Weight</th>
                    <th className="pb-2 text-right">Change</th>
                    <th className="pb-2 text-right">Impact</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-800">
                  {contributors.map((c) => {
                    const isPositive = c.contribution >= 0;
                    return (
                      <tr
                        key={c.symbol}
                        className="hover:bg-gray-800/50 cursor-pointer transition-colors"
                        onClick={() => window.open(`https://www.tradingview.com/chart/?symbol=${c.symbol}&interval=1`, '_blank', 'noopener')}
                      >
                        <td className="py-2">
                          <div className="flex items-center gap-2">
                            <span
                              className="inline-block w-1 h-4 rounded-full"
                              style={{ backgroundColor: isPositive ? '#22c55e' : '#ef4444' }}
                            />
                            <div>
                              <span className="font-semibold text-gray-200">{c.symbol}</span>
                              <span className="text-[10px] text-gray-500 ml-1.5 hidden sm:inline">{c.sector}</span>
                            </div>
                          </div>
                        </td>
                        <td className="py-2 text-right text-gray-400 tabular-nums">
                          {(c.weight * 100).toFixed(2)}%
                        </td>
                        <td className="py-2 text-right tabular-nums">
                          <span className={isPositive ? 'text-green-400' : 'text-red-400'}>
                            {c.changePercent >= 0 ? '+' : ''}{c.changePercent.toFixed(2)}%
                          </span>
                        </td>
                        <td className="py-2 text-right tabular-nums">
                          <span className={`font-semibold ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
                            {isPositive ? '+' : ''}{c.contribution.toFixed(3)}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
